import { useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Eye, EyeOff, Lock } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { useStepUp } from "@/components/auth/StepUp";

export function SsnValue({ ssn, driverId, isAdmin }) {
  const [show, setShow] = useState(false);
  const [full, setFull] = useState(null);
  const requireStepUp = useStepUp();
  if (!ssn) return "—";
  const masked = ssn.startsWith("***") ? ssn : `***-**-${ssn.slice(-4)}`;
  const reveal = async () => {
    if (show) { setShow(false); return; }
    if (!isAdmin) return;
    if (!(await requireStepUp())) return;
    try { const { data } = await api.get(`/drivers/${driverId}`); setFull(data.ssn); setShow(true); } catch (err) { toast.error(formatApiError(err)); }
  };
  return (
    <span className="inline-flex items-center gap-2 font-mono">
      <span data-testid="driver-ssn-value">{show && full ? full : masked}</span>
      {isAdmin && (
        <button type="button" onClick={reveal} className="text-slate-400 hover:text-blue-700" title={show ? "Hide SSN" : "Reveal SSN (admin PIN required)"} data-testid="driver-ssn-toggle">
          {show ? <EyeOff className="h-4 w-4" /> : ssn.startsWith("***") ? <Lock className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
        </button>
      )}
    </span>
  );
}

export const emptyDriver = {
  first_name: "", last_name: "", license_number: "", license_state: "",
  dob: "", dot_number: "", hire_date: "", phone: "", email: "", ssn: "",
  address: { street: "", city: "", county: "", state: "", zip: "" },
  cdl_expiry: "", medical_card_expiry: "", terminal_id: null, leave_return_date: null, terminated_at: null, in_random_pool: true, is_cdl: true, status: "active", notes: "", hazmat_endorsement: "none", tsa_sta_expiry: "", hazmat_training_date: "", cdl_restrictions: [], intrastate_only: false, cdl_class: null, endorsements: [],
};

export const TEST_TYPES = [
  { v: "pre-employment", l: "Pre-Employment" },
  { v: "random", l: "Random" },
  { v: "post-accident", l: "Post-Accident" },
  { v: "reasonable-suspicion", l: "Reasonable Suspicion" },
  { v: "return-to-duty", l: "Return to Duty" },
  { v: "follow-up", l: "Follow-Up" },
];

export function statusBadge(v) {
  const map = {
    negative: "bg-emerald-50 text-emerald-700 border-emerald-200",
    positive: "bg-rose-50 text-rose-700 border-rose-200",
    refusal: "bg-rose-50 text-rose-700 border-rose-200",
    pending: "bg-indigo-50 text-indigo-700 border-indigo-200",
    cancelled: "bg-slate-100 text-slate-600 border-slate-200",
  };
  return <Badge className={`${map[v] || map.pending} border`}>{v}</Badge>;
}

export function expiryBadge(dateStr, renewing) {
  if (!dateStr) return <span className="text-slate-400">—</span>;
  const d = new Date(dateStr);
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const days = Math.round((d - today) / (1000 * 60 * 60 * 24));
  if (renewing && days <= 30) return <Badge className="bg-sky-50 text-sky-700 border border-sky-200" title="Renewal in progress — alerts paused">Renewing · {dateStr}</Badge>;
  if (days < 0) return <Badge className="bg-rose-50 text-rose-700 border border-rose-200">Expired · {dateStr}</Badge>;
  if (days <= 30) return <Badge className="bg-amber-50 text-amber-800 border border-amber-300">{days}d · {dateStr}</Badge>;
  return <span className="font-mono text-xs text-slate-600">{dateStr}</span>;
}

export function Info({ l, v, span }) {
  return (
    <div className={span ? "col-span-2" : ""}>
      <div className="tag-mono text-slate-500">{l}</div>
      <div className="text-slate-900 font-medium">{v}</div>
    </div>
  );
}

